import { MODAL_CONTAINER_ID, PAGE_MAX_WIDTH_CLASS } from '@egw/constants'
import { Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/solid'
import cx from 'classnames'
import { Fragment, useCallback, useMemo, useState, type FC } from 'react'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'
import { Portal } from '../portal/portal'

export interface ModalDrawerProps extends DefaultProps {
  /**
   * ModalDrawer Title
   */
  title: string
  /**
   * Click handler
   */
  onClose: () => void

  /**
   * ModalDrawer open state
   */
  isOpen: boolean

  /**
   * Full Body Content - if true, the children will be rendered as is, otherwise it will be wrapped in a div with a header and a footer
   */
  fullBodyContent?: boolean

  /**
   * Mobile Full Height - makes mobile view slide from right with 100vh height and 4/5 width
   */
  mobileFullHeight?: boolean
}

export const ModalDrawer: FC<ModalDrawerProps> = ({
  // Common default properties for each component
  title,
  onClose,
  isOpen,
  className,
  id,
  testId,
  ariaLabel,
  children,
  fullBodyContent = false,
  mobileFullHeight = false,
}) => {
  // Component base class names. Can be overriden by className property.
  const baseClass = cx('relative z-[101]')

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  // Determine transition classes based on mobileFullHeight prop
  const getTransitionClasses = () => {
    if (mobileFullHeight) {
      // Mobile slides from right like desktop
      return {
        enterFrom: 'translate-x-full',
        enterTo: 'translate-x-0',
        leaveFrom: 'translate-x-0',
        leaveTo: 'translate-x-full',
      }
    }
    // Default behavior: mobile from bottom, desktop from right
    return {
      enterFrom: 'max-md:translate-y-full md:translate-x-full',
      enterTo: 'max-md:translate-y-0 md:translate-x-0',
      leaveFrom: 'max-md:translate-y-0 md:translate-x-0',
      leaveTo: 'max-md:translate-y-full md:translate-x-full',
    }
  }

  const transitions = getTransitionClasses()

  // Determine container classes based on mobileFullHeight prop
  const getContainerClasses = (): string => {
    if (mobileFullHeight) {
      return 'w-[272px] full-viewport-height flex-col flex justify-end content-end'
    }
    return `${PAGE_MAX_WIDTH_CLASS} w-screen max-md:flex max-md:flex-col max-md:justify-end content-end`
  }

  const getModalClasses = (): string => {
    if (mobileFullHeight) {
      return 'pointer-events-auto h-full flex flex-col justify-start bg-white rounded-l-[24px]'
    }
    return 'pointer-events-auto mt-[env(safe-area-inset-top)] max-md:rounded-t-3xl md:rounded-l-3xl flex md:h-full md:max-h-full max-h-[90vh] flex-col justify-start bg-white pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]'
  }

  return (
    <Portal containerId={MODAL_CONTAINER_ID}>
      <Transition.Root show={isOpen} as={Fragment}>
        <div
          {...(testId ? { 'data-testid': testId } : {})}
          {...(id ? { id } : {})}
          {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
          className={finalClasses}
        >
          {/* Backdrop */}

          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              aria-hidden="false"
              role="button"
              tabIndex={0}
              onClick={onClose}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onClose()
                }
              }}
              className="fixed inset-0 bg-zinc-900 bg-opacity-75 transition-opacity"
            />
          </Transition.Child>

          {/* Modal */}
          <div className="pointer-events-none fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div
                className={cx('fixed inset-y-0 right-0 flex max-w-full', {
                  'md:w-[28rem]': !mobileFullHeight,
                  'lg:w-[28rem]': mobileFullHeight,
                })}
              >
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300 sm:duration-300"
                  enterFrom={transitions.enterFrom}
                  enterTo={transitions.enterTo}
                  leave="transform transition ease-in-out duration-300 sm:duration-300"
                  leaveFrom={transitions.leaveFrom}
                  leaveTo={transitions.leaveTo}
                >
                  <div className={getContainerClasses()}>
                    <div className={getModalClasses()}>
                      {/* Body */}
                      {fullBodyContent ? (
                        children
                      ) : (
                        <div className="flex h-full w-full flex-col justify-start py-6 gap-4">
                          <ModalTitle onClose={onClose} title={title} />
                          <div className="flex-1 flex overflow-y-auto scrollbar-hide flex-col px-8">{children}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </Transition.Child>
              </div>
            </div>
          </div>
        </div>
      </Transition.Root>
    </Portal>
  )
}

const ModalTitle: FC<{ title: string; onClose: () => void }> = ({ title, onClose }) => {
  return (
    <div className="flex w-full pl-8 pr-4">
      <h1 data-testid="modalTitle" className="flex-grow typo-primary-xl-light">
        {title}
      </h1>
      <button
        type="button"
        id="closeModal"
        data-testid="closeModal"
        className="size-10 p-2.5"
        onClick={onClose}
        aria-label="Close modal"
      >
        <XMarkIcon className="text-iconmuted h-5 w-5" />
      </button>
    </div>
  )
}

export const useModalState = (isOpenInitially = false) => {
  const [isOpen, setOpen] = useState(isOpenInitially)

  const openModal = useCallback(() => {
    setOpen(true)
  }, [])
  const closeModal = useCallback(() => {
    setOpen(false)
  }, [])

  return useMemo(
    () => ({
      isOpen,
      openModal,
      closeModal,
    }),
    [isOpen, openModal, closeModal],
  )
}
